import type { StreamOutcome } from "./stream";
import { loadSessionMessages, type SessionMessage } from "./use-agents";

/**
 * O QUE FAZER QUANDO O FIO MORRE A MEIO DE UM TURNO.
 *
 * ── O DEFEITO MEDIDO, 06/09 ───────────────────────────────────────────────
 *
 * Turno `builder` das 15:39:49, 226 005 ms, 14 passos, `error` NULO na linha de
 * auditoria: o servidor concluiu e gravou a resposta. O dono viu "a conexão caiu
 * e a resposta não chegou — mande de novo", e mandou. Pagou duas vezes por um
 * trabalho já feito.
 *
 * A causa está do lado do CLIENTE, e o dono mediu-a: a queda acontece quando ele
 * SAI DA TELA — troca de app, bloqueia o telemóvel. O Safari do iOS suspende o
 * JavaScript e fecha as ligações de rede de uma página em segundo plano. Nenhum
 * batimento do servidor salva uma página suspensa: o fio morre AQUI.
 *
 * E o que este ficheiro fazia sobre isso era só reler a sessão, três vezes, em
 * 17 segundos — enquanto o turno ainda tinha 200 segundos pela frente. As três
 * releituras não encontravam nada porque não havia nada ainda: a resposta só
 * seria gravada minutos depois. A busca esgotava e a tela mentia.
 *
 * ── A ORDEM CERTA, E POR QUE ELA É ESTA ───────────────────────────────────
 *
 * 1. VOLTAR A LIGAR ao turno em voo. O servidor deixou de matar um turno quando o
 *    fio cai (`in-process-live-turns.ts`) e guarda tudo o que já disse; voltar a
 *    ligar repete o passado e segue dali. Quando o turno ainda corre — que é o
 *    caso comum de quem volta ao telemóvel — esta é a recuperação EXACTA, e não
 *    tem prazo nenhum: o fio novo dura o que o turno durar.
 * 2. RELER A SESSÃO. Só quando o servidor responde `none` é que não há turno vivo
 *    — e aí, se houve um, a resposta já está gravada, porque o servidor arquiva
 *    ANTES de mandar o quadro `done` (`agent-run.controller.ts`).
 *
 * A ordem inversa é o que estava aqui e é o que falha: reler primeiro pergunta
 * por uma resposta que ainda está a ser escrita.
 */

/**
 * O RECUO ENTRE TENTATIVAS.
 *
 * Isto NÃO é o orçamento do turno, e é a confusão que o desenho antigo fazia. Um
 * `reattach` que pega segue o turno até ao fim sem olhar ao relógio — estes
 * números só medem quanto tempo se insiste enquanto a REDE ainda está morta.
 * Dois minutos de insistência cobrem um elevador, um túnel e um telemóvel a
 * reencontrar a torre; um turno de 424 s (o máximo medido no painel) é coberto
 * pela ligação que a primeira tentativa bem sucedida abre, e não por esta lista.
 */
export const RECOVERY_DELAYS: readonly number[] = [
	1_000, 2_000, 4_000, 8_000, 15_000, 30_000, 30_000, 30_000,
];

/** Como a resposta voltou — ou `null`, que é a única falha honesta. */
export type Recovered =
	/** Reatámos o fio: os quadros voltaram a passar e quem desenha já desenhou. */
	| { how: "live" }
	/** O turno acabou enquanto ninguém via; isto é o que ficou arquivado. */
	| { how: "archived"; replies: SessionMessage[] }
	| null;

export interface RecoverInput {
	onMessages?: (messages: SessionMessage[]) => void;
	/** Onde a resposta terá sido arquivada. `null` quando não havia thread. */
	sessionId: string | null;
	/** A pergunta do turno caído — a âncora que a acha na thread. */
	prompt: string;
	/** Volta a ligar-se ao turno em voo deste agente (`attachToLiveTurn`). */
	reattach: (signal: AbortSignal) => Promise<StreamOutcome>;
	/** Cancela a busca: uma mensagem nova ou um painel fechado tornam-na inútil. */
	signal?: AbortSignal;
	/** Injectáveis para os testes. A tela não passa nenhum destes três. */
	load?: (sessionId: string) => Promise<SessionMessage[]>;
	wait?: (ms: number, signal?: AbortSignal) => Promise<void>;
	delays?: readonly number[];
}

/**
 * Insiste até reaver a resposta, ou desiste.
 *
 * `null` — e só então — é a tela poder declarar o turno perdido. Mesmo aí a frase
 * não manda reenviar: o servidor pode ter terminado e o processo pode ter caído
 * a meio, e as duas coisas são indistinguíveis daqui.
 */
export async function recoverTurn(input: RecoverInput): Promise<Recovered> {
	const load = input.load ?? loadSessionMessages;
	const wait = input.wait ?? waitOrVisible;

	for (const ms of input.delays ?? RECOVERY_DELAYS) {
		await wait(ms, input.signal);
		if (input.signal?.aborted) return null;

		// 1. O turno ainda corre? Uma falha aqui não é veredito nenhum: a rede que
		//    derrubou o fio ainda está a recuperar, e insistir é o trabalho.
		const outcome = await input
			.reattach(input.signal ?? new AbortController().signal)
			.catch((): StreamOutcome => "dropped");
		if (input.signal?.aborted) return null;
		if (outcome === "done" || outcome === "error") return { how: "live" };

		// 2. `none` é o servidor a dizer que não há turno em voo. Se houve um, ele
		//    já arquivou a resposta antes de fechar — então é agora que se relê.
		if (outcome === "none" && input.sessionId) {
			const messages = await load(input.sessionId).catch(() => null);
			if (input.signal?.aborted) return null;
			input.onMessages?.(messages ?? []);
			const replies = messages ? repliesAfter(messages, input.prompt) : null;
			if (replies) return { how: "archived", replies };
		}
	}
	return null;
}

/**
 * As falas do assistente arquivadas DEPOIS da última vez que isto foi perguntado.
 *
 * A última, e não a primeira: "regenerar" guarda toda tentativa, e uma resposta
 * velha à mesma pergunta não pode passar por resposta do turno que caiu.
 */
function repliesAfter(
	messages: readonly SessionMessage[],
	prompt: string,
): SessionMessage[] | null {
	let anchor = -1;
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (message?.role === "user" && message.content === prompt) {
			anchor = i;
			break;
		}
	}
	if (anchor < 0) return null;
	const replies = messages.slice(anchor + 1).filter((m) => m.role === "assistant");
	return replies.length ? replies : null;
}

/**
 * Um recuo que voltar à tela corta pela frente.
 *
 * É o caso inteiro deste lote: o Safari suspende a página em segundo plano, então
 * o temporizador só volta a correr quando a pessoa volta — e quando ela volta, a
 * reconciliação tem de ser AGORA, não quando o recuo se lembrar.
 */
function waitOrVisible(ms: number, signal?: AbortSignal): Promise<void> {
	return new Promise((resolve) => {
		const done = (): void => {
			clearTimeout(timer);
			document.removeEventListener("visibilitychange", onVisible);
			signal?.removeEventListener("abort", done);
			resolve();
		};
		const timer = setTimeout(done, ms);
		const onVisible = (): void => {
			if (document.visibilityState === "visible") done();
		};
		document.addEventListener("visibilitychange", onVisible);
		signal?.addEventListener("abort", done);
	});
}
